import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CompanySettings, DefaultAssignmentSkip } from '@pxtra-client-lib-legacy/gen';
import { DataViewEditBox, useForm } from '../../../../lib/components';
import { useStore } from '../../../../hooks/useStore';
import { useCompanySettingsUpdate } from '../../../../queries/company/mutations';
import { useListBenefitSet } from '../../../../queries/benefit/benefitSet';
import { enqueueSnackbar } from 'notistack';
import { CompanyDefaultValuesForm } from './form';
import { CompanyDefaultValuesView } from './view';
import { useCompanySettings } from '../../../../queries/company';
import { ApplyDefaultsDialog } from './applyDefaultsDialog';
import { ApplyingDefaultsDialog } from './applyingDefaultsDialog';
import { SkippedDefaultsDialog } from './skippedDefaultsDialog';
import Util from '../../../../misc/util';

type DefaultValues = {
  defaultBenefitBudget?: number | null;
  defaultBenefitSet?: string | null;
};

export const CompanyDefaultValues = observer(() => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { companyStore } = useStore();
  const { data: companySettings } = useCompanySettings(companyStore.company?.id ?? '');
  const { data: benefitSets } = useListBenefitSet(companyStore.company?.id ?? '');

  const [pendingValues, setPendingValues] = useState<DefaultValues | null>(null);
  const [skippedEmployees, setSkippedEmployees] = useState<DefaultAssignmentSkip[]>([]);
  const [isApplyingToExistingEmployees, setIsApplyingToExistingEmployees] =
    useState<boolean>(false);

  const showResultSnackbar = (settings: CompanySettings) => {
    const applied = settings.applied_defaults;
    const budgetCount = applied?.budget_assigned_count ?? 0;
    const benefitSetCount = applied?.benefit_set_assigned_count ?? 0;

    if (!applied || (budgetCount === 0 && benefitSetCount === 0)) {
      enqueueSnackbar(t('company:SAVING_DEFAULT_VALUES'), { variant: 'success' });
      return;
    }

    const nextMonth = Util.formatDateToMonthYear(
      Util.monthFieldToDate(applied.start_accounting_period) ?? new Date()
    );
    const message =
      budgetCount > 0 && benefitSetCount > 0
        ? t('company:APPLY_DEFAULTS_SUCCESS_BOTH', {
            budgetCount,
            count: benefitSetCount,
            nextMonth
          })
        : budgetCount > 0
          ? t('company:APPLY_DEFAULTS_SUCCESS_BUDGET', { count: budgetCount, nextMonth })
          : t('company:APPLY_DEFAULTS_SUCCESS_BENEFIT_SET', {
              count: benefitSetCount,
              nextMonth
            });
    enqueueSnackbar(message, { variant: 'success' });
  };

  const { mutate: updateDefaultValues, isPending } = useCompanySettingsUpdate({
    onSuccess: (settings: CompanySettings) => {
      queryClient.invalidateQueries({
        queryKey: ['company', companyStore.company?.id, 'settings']
      });
      showResultSnackbar(settings);
      setSkippedEmployees(settings.applied_defaults?.skipped ?? []);
    },
    onError: () => {
      enqueueSnackbar(t('common:savingError'), { variant: 'error' });
    },
    onSettled: () => {
      setIsApplyingToExistingEmployees(false);
    }
  });

  const defaultBenefitSetName = benefitSets?.results.find(
    (bs) => bs.id === companySettings?.default_benefit_set
  )?.name;

  const defaultValueData = {
    defaultBenefitBudget: companySettings?.default_benefit_budget,
    defaultBenefitSet: companySettings?.default_benefit_set,
    defaultBenefitSetName: defaultBenefitSetName
  };
  const [canSubmit, setCanSubmit] = useState<boolean>(false);
  const form = useForm();

  const save = (values: DefaultValues, applyToExistingEmployees: boolean) => {
    setIsApplyingToExistingEmployees(applyToExistingEmployees);
    updateDefaultValues({
      company: companyStore.company?.id ?? '',
      settings: {
        default_benefit_budget: values.defaultBenefitBudget,
        default_benefit_set: values.defaultBenefitSet || null,
        apply_to_existing_employees: applyToExistingEmployees
      }
    });
    setPendingValues(null);
  };

  const handleSubmit = (values: DefaultValues) => {
    if (!values.defaultBenefitBudget && !values.defaultBenefitSet) {
      save(values, false);
      return;
    }
    setPendingValues(values);
  };

  const renderView = observer(() => <CompanyDefaultValuesView data={defaultValueData} />);

  const renderEdit = () => {
    return (
      <CompanyDefaultValuesForm
        benefitSets={benefitSets}
        initialValues={defaultValueData}
        formRef={form.formRef}
        onStateChanged={setCanSubmit}
        onSubmit={(values) => handleSubmit(values as DefaultValues)}
        useCustomButton
      />
    );
  };

  return (
    <>
      <DataViewEditBox
        title={t('company:DEFAULT_VALUES')}
        subtitle={t('company:DEFAULT_VALUES_SUBTITLE')}
        view={renderView}
        edit={renderEdit}
        canEdit
        canSubmit={canSubmit}
        onSubmit={form.handleSubmit}
        onCancel={form.handleReset}
      />
      <ApplyDefaultsDialog
        open={pendingValues !== null}
        hasBudget={Boolean(pendingValues?.defaultBenefitBudget)}
        hasBenefitSet={Boolean(pendingValues?.defaultBenefitSet)}
        onApply={() => pendingValues && save(pendingValues, true)}
        onDecline={() => pendingValues && save(pendingValues, false)}
      />
      <ApplyingDefaultsDialog open={isPending && isApplyingToExistingEmployees} />
      <SkippedDefaultsDialog skipped={skippedEmployees} onClose={() => setSkippedEmployees([])} />
    </>
  );
});
